import { API, Auth, graphqlOperation, Storage } from "aws-amplify";
import _ from "lodash";
import * as queries from "graphql/queries";
import * as customQueries from "graphql/customQueries";
import * as mutations from "graphql/mutations";
import * as customMutations from "graphql/customMutations";
import * as parsers from "services/apiParsers";
import { sleep, stripFilename } from "services/utils";
import { AssetStatus } from "models";
import {
  ACTIVE_CAMPAIGNS_FILTER,
  INACTIVE_CAMPAIGNS_FILTER,
  PROCESSED_ASSETS_FILTER,
} from "constants/graphqlFilters";
import { t } from "../i18n";
import { toast } from "components/Toast/Toast";

const NO_DATA_ERROR = t("no_data_error", { ns: "common" });

export const deleteUser = async (userId) => {
  try {
    /** Delete user based on user id */
    const result = await API.post("CommonQueries", "/DeleteCognitoUser", {
      headers: {
        "Content-Type": "application/json",
      },
      body: {
        userId,
      },
    });
    toast(undefined, "User deleted succesfully");
    return result;
  } catch (err) {
    console.error("Error when deleting user:", err);
    toast("error", "Error occured when deleting user");

    return {
      errorMessage: "User delete failed",
    };
  }
};

export const doHealthCheck = async () => {
  const response = await API.get("CommonQueries", "/health-check");
  return response;
};

export const getGroups = async () => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    /** Fetch users based on group name */
    const result = await API.get("AdminQueries", `/listGroups`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": user.signInUserSession.accessToken.jwtToken,
      },
    });
    const userGroups = result.Groups;
    return userGroups;
  } catch (err) {
    toast("error", "Error fetching groups");
    console.error("Error fetching groups:", err);
  }
};

export const verifyRecaptcha = async (token, sitekey, action = "LOGIN") => {
  try {
    const res = await API.post("CommonQueries", "/VerifyRecaptcha", {
      body: { token, sitekey, action },
      headers: { "Content-Type": "application/json" },
    });
    return { score: res.score };
  } catch (err) {
    console.error("reCAPTCHA verify failed:", err);
    return { error: true };
  }
};

export const getUsersInGroup = async (groupname) => {
  let nextToken;
  let apiName = "AdminQueries";
  let path = "/listUsersInGroup";
  let users = [];
  const user = await Auth.currentAuthenticatedUser();
  let myInit = {
    queryStringParameters: {
      groupname: groupname,
      limit: 60,
    },
    headers: {
      "Content-Type": "application/json",
      "Authorization": user.signInUserSession.accessToken.jwtToken,
    },
  };

  try {
    const response = await API.get(apiName, path, myInit);
    users = users.concat(response.Users);
    nextToken = response.NextToken;
  } catch (err) {
    console.error("Error fetching users in group", err);
  }
  if (nextToken) {
    try {
      myInit.queryStringParameters.token = nextToken;
      const response = await API.get(apiName, path, myInit);
      nextToken = response.NextToken;
      users = users.concat(response.Users);
    } catch (err) {
      console.error("Error fetching users in group", err);
    }
    while (nextToken) {
      try {
        myInit.queryStringParameters.token = nextToken;
        const response = await API.get(apiName, path, myInit);
        nextToken = response.NextToken;
        users = users.concat(response.Users);
      } catch (err) {
        console.error("Error fetching users in group", err);
      }
    }
  }
  return users;
};

export const removeUserFromCognitoGroup = async ({
  groupname,
  username,
  userpoolid,
  UserEmail,
  isUpdatingUser,
}) => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    /** Fetch users based on group name */
    const result = await API.post("AdminQueries", `/removeUserFromGroup`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": user.signInUserSession.accessToken.jwtToken,
      },
      body: {
        username,
        groupname,
        userpoolid,
      },
    });
    !isUpdatingUser &&
      toast(undefined, `User ${UserEmail} succesfully removed from group ${groupname}`);
    return result;
  } catch (err) {
    toast("error", "Error removing user from group.");
    console.error("Error removing user from group:", err);
  }
};

export const addUserToCognitoGroup = async ({
  groupname,
  username,
  userpoolid,
  UserEmail,
  UserOriginalGroupName,
}) => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    /** Fetch users based on group name */
    const result = await API.post("AdminQueries", `/addUserToGroup`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": user.signInUserSession.accessToken.jwtToken,
      },
      body: {
        username,
        groupname,
        userpoolid,
      },
    });
    toast(
      undefined,
      `User ${UserEmail} succesfully moved from group ${UserOriginalGroupName} to group ${groupname}`
    );
    return result;
  } catch (err) {
    toast("error", "Error adding user to group.");
    console.error("Error adding user to group:", err);
  }
};

export const disableUser = async ({ username, UserEmail }) => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const result = await API.post("AdminQueries", `/disableUser`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": user.signInUserSession.accessToken.jwtToken,
      },
      body: {
        username,
      },
    });
    toast(undefined, `User ${UserEmail} succesfully disabled`);
    return result;
  } catch (err) {
    toast("error", "Error disabling user");
    console.error("Error disabling user", err);
  }
};

export const enableUser = async ({ username, UserEmail }) => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const result = await API.post("AdminQueries", `/enableUser`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": user.signInUserSession.accessToken.jwtToken,
      },
      body: {
        username,
      },
    });
    toast(undefined, `User ${UserEmail} succesfully enabled`);
    return result;
  } catch (err) {
    toast("error", "Error enabling user");
    console.error("Error enabling user", err);
  }
};

export const getActiveUser = async () => {
  const user = await Auth.currentAuthenticatedUser();
  const userGroups = user.signInUserSession.idToken.payload["cognito:groups"];

  const data = {
    username: user.username,
    email: user.attributes.email,
    email_verified: user.attributes.email_verified,
    phone_number: user.attributes.phone_number,
    phone_number_verified: user.attributes.phone_number_verified,
    isAdmin: userGroups.includes("admin"),
    isPowerAdmin: userGroups.includes("poweradmin"),
    isSuperAdmin: userGroups.includes("superadmin"),
    isContributor: userGroups.includes("contributors"),
    groups: userGroups,
  };
  return { data };
};

export const getGroupsForUser = async (username) => {
  const user = await Auth.currentAuthenticatedUser();
  let group;
  const headers = {
    headers: {
      "Content-Type": "application/json",
      "Authorization": user.signInUserSession.accessToken.jwtToken,
    },
  };
  const errorHandler = () => {
    console.error("Couldn't load users");
    throw new Error("Couldn't load users");
  };
  try {
    const response = await API.get(
      "AdminQueries",
      `/listGroupsForUser?username=${username}`,
      headers
    );
    group = response;
  } catch {
    errorHandler();
  }
  return group;
};

// DynamoDB User, not Cognito!
export const getUserDB = async (id) => {
  let result, errorMessage;

  try {
    const response = await runGraphql(graphqlOperation(customQueries.getUserDB, { id }), "getUser");
    result = response.data;
    errorMessage = response.errorMessage;
  } catch (error) {
    console.log("Error getting user from db:", error);
  }

  return {
    data: result,
    errorMessage: errorMessage,
  };
};

export const getUsers = async () => {
  let data = [];
  let nextToken;
  let errorMessage;

  do {
    try {
      const response = await runGraphql(
        graphqlOperation(customQueries.listUsers, { nextToken }),
        "listUsers",
        true
      );
      data = [...data, ...response.data];
      nextToken = response.nextToken;
    } catch (error) {
      console.error("Error fetching users", error);
      errorMessage = error.message || "An error occurred while fetching users.";
      nextToken = null;
    }
  } while (nextToken);

  return {
    data: data.filter(Boolean),
    errorMessage: errorMessage,
  };
};

const runGraphql = async (operation, dataKey, isList) => {
  let response;

  try {
    response = await API.graphql(operation);
  } catch (err) {
    response = err;
  }

  if (!isList) {
    const data = _.get(response, "data." + dataKey);
    const errorMessage = response.errors
      ? response.errors.map((err) => err.message).join("\n")
      : undefined;

    if (!data) throw new Error(errorMessage || NO_DATA_ERROR);

    return {
      data,
      errorMessage,
    };
  } else {
    const data = _.get(response, "data." + dataKey + ".items");
    const nextToken = _.get(response, "data." + dataKey + ".nextToken");
    const errorMessage = response.errors
      ? response.errors.map((err) => err.message).join("\n")
      : undefined;

    if (!data) throw new Error(errorMessage || NO_DATA_ERROR);

    return {
      data,
      errorMessage,
      nextToken,
    };
  }
};

export const getCampaign = async (id) => {
  return await runGraphql(graphqlOperation(customQueries.getCampaign, { id }), "getCampaign");
};

const getNextAssetsOfCampaign = async (campaignId, assetsNextToken, limit = 100) => {
  const result = await runGraphql(
    graphqlOperation(customQueries.getCampaignsAssets, { id: campaignId, assetsNextToken, limit }),
    "getCampaign"
  );

  return result.data.assets;
};

export const getAssetsOfCampaign = async (id) => {
  // Create result object
  const result = { items: [] };
  let assetsNextToken = null;

  // Fetch the campaign we want the assets of
  const response = await runGraphql(
    graphqlOperation(customQueries.getCampaign, { id }),
    "getCampaign"
  );

  // Write assets to result object
  result.items = [...result.items, ...response.data.assets.items];
  assetsNextToken = response.data.assets.nextToken;

  // Repeat fetching more assets until we no longer have nextToken
  do {
    const assetsResult = await getNextAssetsOfCampaign(id, assetsNextToken);
    result.items = [...result.items, ...assetsResult.items];
    assetsNextToken = assetsResult.nextToken != null ? assetsResult.nextToken : null;
  } while (assetsNextToken !== null);

  // Finally parse assets before passing result out.
  return await Promise.all(result.items.filter((item) => !!item).map(parsers.parseAsset));
};

export const getAssetsOfCampaignContributor = async (id) => {
  // Create result object
  const result = { items: [] };
  let assetsNextToken = null;

  // Fetch the campaign we want the assets of
  const response = await runGraphql(
    graphqlOperation(customQueries.getCampaignContributor, { id }),
    "getCampaign"
  );

  // Write assets to result object
  result.items = [...result.items, ...response.data.assets.items];
  assetsNextToken = response.data.assets.nextToken;

  // Repeat fetching more assets until we no longer have nextToken
  do {
    const assetsResult = await getNextAssetsOfCampaign(id, assetsNextToken);
    result.items = [...result.items, ...assetsResult.items];
    assetsNextToken = assetsResult.nextToken != null ? assetsResult.nextToken : null;
  } while (assetsNextToken !== null);

  // Finally parse assets before passing result out.
  return await Promise.all(result.items.filter((item) => !!item).map(parsers.parseAsset));
};

export const getApprovalsOfCampaign = async (id) => {
  let items = [];
  let errorMessage;
  let nextToken;

  try {
    do {
      const result = await runGraphql(
        graphqlOperation(customQueries.listCampaignsApprovals, { campaignID: id, nextToken }),
        "listApprovals"
      );
      if (result && result.data && result.data.items) {
        items = items.concat(result.data.items);
      }
      nextToken = result.data.nextToken ? result.data.nextToken : null;
      errorMessage = result.errorMessage;
    } while (nextToken);
  } catch (error) {
    console.log("error getting approvals of campaign:", error);
  }

  return {
    data: items,
    errorMessage: errorMessage,
  };
};

export const getCampaignApprovalInformation = async (id) => {
  let result;
  try {
    result = await runGraphql(
      graphqlOperation(customQueries.getCampaignApprovalInfo, { id }),
      "getCampaign"
    );
  } catch (error) {
    console.log(error);
  }
  return result;
};

export const getCampaignByDeliveryCode = async (deliveryCode, username) => {
  const activeUser = await getActiveUser();
  try {
    const result = await API.post("CommonQueries", "/graphqlOperations/getCampaignByDeliveryCode", {
      body: {
        deliveryCode,
        username,
        isAdmin: activeUser.data.isAdmin || activeUser.data.isPowerAdmin,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });
    return {
      data: result.campaign,
      errorMessage: result.errorMessage,
    };
  } catch (err) {
    console.error("Error getting campaign by delivery code: ", JSON.stringify(err, null, 2));

    return {
      data: undefined,
      errorMessage: t("something_went_wrong", { ns: "common" }),
    };
  }
};

export const createCampaign = async (input) => {
  const result = await runGraphql(
    graphqlOperation(mutations.createCampaign, { input }),
    "createCampaign"
  );

  return result;
};

export const updateCampaign = async (input) => {
  // if status is CANCELLED or ARCHIVED, we need to change the key statusGroup to INACTIVE. Otherwise it is ACTIVE
  const inactiveStatuses = ["CANCELLED", "ARCHIVED"];

  const updatedInput = {
    ...input,
    statusGroup: inactiveStatuses.includes(input.status) ? "INACTIVE" : "ACTIVE",
  };

  const result = await runGraphql(
    graphqlOperation(mutations.updateCampaign, { input: updatedInput }),
    "updateCampaign"
  );

  return result;
};

export const search = async (searchTerm) => {
  const results = {
    campaigns: { name: "Campaigns", results: [] },
    assets: { name: "Assets", results: [] },
  };
  let nextToken;
  let errorMessage;

  do {
    const result = await runGraphql(
      graphqlOperation(queries.listCampaigns, { nextToken, filter: { name: { in: searchTerm } } }),
      "listCampaigns",
      true
    );
    results.campaigns.results = results.campaigns.results.concat(
      result.data.filter((item) => !!item)
    );
    nextToken = result.nextToken;
    if (result.errorMessage)
      errorMessage = errorMessage
        ? errorMessage + "\n\n" + result.errorMessage
        : result.errorMessage;
  } while (nextToken);

  nextToken = undefined;

  do {
    const result = await runGraphql(
      graphqlOperation(queries.listAssets, { nextToken, filter: { name: { in: searchTerm } } }),
      "listAssets",
      true
    );

    results.assets.results = results.assets.results.concat(result.data.filter((item) => !!item));
    nextToken = result.nextToken;
    if (result.errorMessage)
      errorMessage = errorMessage
        ? errorMessage + "\n\n" + result.errorMessage
        : result.errorMessage;
  } while (nextToken);

  results.assets.results = await Promise.all(results.assets.results.map(parsers.parseAsset));

  return {
    data: results,
    errorMessage: errorMessage,
  };
};

export const listCampaigns = async (payload) => {
  let data = [];
  let nextToken = (payload && payload.nextToken) || undefined;
  let filter = (payload && payload.filter) || undefined;
  let errorMessage;
  do {
    const result = await listOneQueryOfCampaigns({ nextToken, filter });
    data = data.concat(result.data);
    nextToken = result.nextToken;
    if (result.errorMessage)
      errorMessage = errorMessage
        ? errorMessage + "\n\n" + result.errorMessage
        : result.errorMessage;
  } while (nextToken);

  return {
    data: data.filter((item) => !!item),
    errorMessage: errorMessage,
  };
};

export const listOneQueryOfCampaigns = async (payload = {}) => {
  const nextToken = payload.nextToken;
  const filter = payload.filter;
  const statusGroup = _.isEqual(filter, ACTIVE_CAMPAIGNS_FILTER)
    ? "ACTIVE"
    : _.isEqual(filter, INACTIVE_CAMPAIGNS_FILTER)
    ? "INACTIVE"
    : undefined;

  const result = await runGraphql(
    graphqlOperation(
      statusGroup
        ? customQueries.listCampaignsByStatusGroup
        : customQueries.listCampaignsByCreatedAt,
      statusGroup
        ? {
            statusGroup,
            sortDirection: "DESC",
            nextToken,
          }
        : {
            dummyType: "Campaign",
            sortDirection: "DESC",
            filter,
            nextToken,
          }
    ),
    statusGroup ? "listCampaignsByStatusGroup" : "listCampaignsByCreatedAt",
    true
  );

  return {
    data: (result.data || []).filter((item) => !!item),
    errorMessage: result.errorMessage,
    nextToken: result.nextToken,
  };
};

export const listAllCampaigns = async (payload) => {
  let data = [];
  let nextToken = (payload && payload.nextToken) || undefined;
  let filter = (payload && payload.filter) || undefined;
  let errorMessage;

  do {
    const result = await runGraphql(
      graphqlOperation(customQueries.listCampaigns, {
        dummyType: "Campaign",
        sortDirection: "DESC",
        filter,
        nextToken,
      }),
      "listCampaigns",
      true
    );
    data = data.concat(result.data);
    nextToken = result.nextToken;
    if (result.errorMessage)
      errorMessage = errorMessage
        ? errorMessage + "\n\n" + result.errorMessage
        : result.errorMessage;
  } while (nextToken);

  return {
    data: data.filter((item) => !!item),
    errorMessage: errorMessage,
  };
};
const LIST_ASSETS_DEFAULT_LIMIT = 500;

/**
 * Does listAssets queries until all assets have been queried (i.e. no nextToken returned from the query)
 * @param {object} [payload={}] - Optional payload object
 * @param {object} payload.filter - Filter object for the query. More info: https://docs.amplify.aws/lib/graphqlapi/query-data/q/platform/js/#filtering-queries
 * @returns {object} Object containing an array of parsed assets and possible error message as a string.
 */
export const listAssets = async (payload = {}) => {
  let data = [];
  let nextToken;
  let errorMessage;
  const filter = payload.filter;

  do {
    const result = await runGraphql(
      graphqlOperation(customQueries.listAssetsByCreatedAt, {
        dummyType: "Asset",
        sortDirection: "DESC",
        filter,
        nextToken,
        limit: LIST_ASSETS_DEFAULT_LIMIT,
      }),
      "listAssetsByCreatedAt",
      true
    );
    data = data.concat(result.data);

    nextToken = result.nextToken;
    if (result.errorMessage)
      errorMessage = errorMessage
        ? errorMessage + "\n\n" + result.errorMessage
        : result.errorMessage;
  } while (nextToken);
  if (errorMessage) console.log(errorMessage);
  return {
    data: await Promise.all(data.filter((item) => !!item).map(parsers.parseAsset)),
    errorMessage: errorMessage,
  };
};

/**
 * Does one query of list assets
 * @param {object} [payload={}] - Optional payload object
 * @param {string} payload.nextToken - Token used to query the next list of assets. If not defined, query starts from the beginning
 * @param {object} payload.filter - Filter object for the query. More info: https://docs.amplify.aws/lib/graphqlapi/query-data/q/platform/js/#filtering-queries
 * @returns {object} Object containing an array of parsed assets, possible error message as a string and possible next token as a string if there's more to be queried
 */
export const listOneQueryOfAssets = async (payload = {}) => {
  const nextToken = payload.nextToken;
  const filter = payload.filter;

  const result = await runGraphql(
    graphqlOperation(customQueries.listAssetsByCreatedAt, {
      dummyType: "Asset",
      sortDirection: "DESC",
      filter,
      nextToken,
      limit: LIST_ASSETS_DEFAULT_LIMIT,
    }),
    "listAssetsByCreatedAt",
    true
  );
  return {
    data: await Promise.all((result.data || []).filter((item) => !!item).map(parsers.parseAsset)),
    errorMessage: result.errorMessage,
    nextToken: result.nextToken,
  };
};

export const getAsset = async (id) => {
  const result = await runGraphql(graphqlOperation(queries.getAsset, { id }), "getAsset");

  return {
    data: await parsers.parseAsset(result.data),
    errorMessage: result.errorMessage,
  };
};

export const getAssetStatus = async (id) => {
  const result = await runGraphql(graphqlOperation(queries.getAsset, { id }), "getAsset");

  return {
    data: result.data.status,
    errorMessage: result.errorMessage,
  };
};

/**
 * Does listAssets queries until all assets have been queried (i.e. no nextToken returned from the query)
 * @param {object} [payload={}] - Optional payload object
 * @param {object} payload.filter - Filter object for the query. More info: https://docs.amplify.aws/lib/graphqlapi/query-data/q/platform/js/#filtering-queries
 * @returns {object} Object containing an array of parsed assets and possible error message as a string.
 */
export const listOldAssets = async (payload = {}) => {
  let data = [];
  let nextToken;
  let errorMessage;
  const filter = payload.filter;

  do {
    const result = await runGraphql(
      graphqlOperation(customQueries.listAssetsByCreatedAt, {
        dummyType: "Asset",
        sortDirection: "DESC",
        filter,
        nextToken,
        limit: LIST_ASSETS_DEFAULT_LIMIT,
      }),
      "listAssetsByCreatedAt",
      true
    );
    data = data.concat(result.data);
    nextToken = result.nextToken;
    if (result.errorMessage)
      errorMessage = errorMessage
        ? errorMessage + "\n\n" + result.errorMessage
        : result.errorMessage;
  } while (nextToken);
  if (errorMessage) console.log(errorMessage);
  return {
    data: await Promise.all(data.filter((item) => !!item).map(parsers.parseAsset)),
    errorMessage: errorMessage,
  };
};

export const waitForAssetQCStatus = async (id, timeout = 180000) => {
  const INTERVAL = 3000;
  const startTime = Date.now();
  let tryTime = 0,
    tries = 0;

  do {
    await sleep(INTERVAL);
    const { data, errorMessage } = await getAsset(id);
    const status = data.status;
    if (
      status !== AssetStatus.ARRIVED &&
      status !== AssetStatus.QC_PENDING &&
      data.thumbnailComplete
    ) {
      return {
        data,
        errorMessage,
      };
    }

    tryTime = Date.now() - startTime;
    tries++;
  } while (tryTime < timeout);

  throw new Error(t("timeout_error", { ns: "common", tries }));
};

export const getUnusedDeliveryCode = async () => {
  try {
    const response = await API.post("CommonQueries", "/CreateDeliveryCode", {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (err) {
    console.error("Error getting campaign by delivery code: ", JSON.stringify(err, null, 0));
    throw new Error(t("something_went_wrong", { ns: "common" }));
  }
};

export const createAsset = async (input, updateUploadProgressArr, uploadProgressObj) => {
  const name = stripFilename(input.name, true);
  input.name = name;

  const result = await runGraphql(
    graphqlOperation(mutations.createAsset, { input: parsers.unparseAsset(input) }),
    "createAsset"
  );

  if (input.file) {
    // Save media to the incoming folder
    const mediaS3Path = `incoming/${result.data.campaignID}/${result.data.id}/${name}`;
    await Storage.put(mediaS3Path, input.file, {
      progressCallback(progress) {
        const uploadProgress = {
          ...uploadProgressObj,
          loaded: progress.loaded,
        };
        updateUploadProgressArr(uploadProgress);
      },
    });

    // Generate thumbnail path
    const thumbnailS3Path = generateThumbnailPath(result.data.campaignID, result.data.id, name);

    // Generate path for the final destination of the asset which will be added to the db
    const finalMediaS3Path = `assets/${result.data.campaignID}/${result.data.id}/${name}`;

    // Update media and thumbnail path for asset
    const updateResult = await updateAsset({
      id: result.data.id,
      media: finalMediaS3Path,
      thumbnail: thumbnailS3Path,
    });

    return updateResult;
  }

  return result;
};

export const updateAsset = async (input, media) => {
  if (media) {
    // Save media
    const mediaS3Path = `incoming/${input.campaignID}/${input.id}/${media.name}`;
    await Storage.put(mediaS3Path, media);

    const thumbnailS3Path = generateThumbnailPath(input.campaignID, input.id, media.name);

    // Generate path for the final destination of the asset which will be added to the db
    const finalMediaS3Path = `assets/${input.campaignID}/${input.id}/${media.name}`;

    // Update media and thumbnail path for asset
    input = { ...input, media: finalMediaS3Path, thumbnail: thumbnailS3Path };
  }

  const result = await runGraphql(
    graphqlOperation(mutations.updateAsset, { input: parsers.unparseAsset(input) }),
    "updateAsset"
  );

  return {
    data: await parsers.parseAsset(result.data),
    errorMessage: result.errorMessage,
  };
};

export const updateCampaignViewers = async (id, updatedViewers) => {
  if (updatedViewers && updatedViewers.length > 0) {
    try {
      let data, errorMessage;
      const updateResult = await runGraphql(
        graphqlOperation(mutations.updateCampaign, {
          input: {
            id: id,
            viewers: updatedViewers,
          },
        }),
        "updateCampaign"
      );
      data = updateResult.data;
      errorMessage = updateResult.errorMessage;

      return {
        data,
        errorMessage,
      };
    } catch (error) {
      console.log("Could not update campaign viewers:\n", error);
    }
  }
};

// Takes id of asset to update and string array of viewer userIds to add
export const updateAssetViewers = async (id, newViewerIds) => {
  if (newViewerIds && newViewerIds.length > 0) {
    try {
      const response = await getAsset(id);
      let viewers = response.data.viewers ?? [];

      // Only do update if there are something new to add to the list
      if (newViewerIds.some((viewerId) => !viewers.includes(viewerId))) {
        let data, errorMessage;
        viewers = Array.from(new Set([...viewers, ...newViewerIds]));
        const updateResult = await runGraphql(
          graphqlOperation(customMutations.updateAssetViewers, {
            input: {
              id: id,
              viewers: viewers,
            },
          }),
          "updateAsset"
        );
        data = updateResult.data;
        errorMessage = updateResult.errorMessage;
        return {
          data,
          errorMessage,
        };
      }
    } catch (error) {
      console.log("Could not update asset viewers:\n", error);
    }
  }
  return;
};

// Generates thumbnail path
function generateThumbnailPath(campaignID, assetID, fileName) {
  // Thumbnail is always .jpg -> replace file extension with .jpg
  let fileNameWithJpgExtension = fileName.split(".");
  fileNameWithJpgExtension.splice(-1, 1, "jpg");
  fileNameWithJpgExtension = fileNameWithJpgExtension.join(".");

  return `thumbnails/${campaignID}/${assetID}/${fileNameWithJpgExtension}`;
}

export const listNativeResolutions = async () => {
  let data = [];
  let nextToken;
  let errorMessage;

  do {
    const result = await runGraphql(
      graphqlOperation(customQueries.listNativeResolutionsWithCampaigns, { nextToken }),
      "listNativeResolutions",
      true
    );
    data = data.concat(result.data);
    nextToken = result.nextToken;
    if (result.errorMessage)
      errorMessage = errorMessage
        ? errorMessage + "\n\n" + result.errorMessage
        : result.errorMessage;
  } while (nextToken);

  return {
    data: data.filter((item) => !!item),
    errorMessage: errorMessage,
  };
};

export const createNativeResolution = async (input) => {
  const { data, errorMessage } = await runGraphql(
    graphqlOperation(mutations.createNativeResolution, { input }),
    "createNativeResolution"
  );

  return { data, errorMessage };
};

export const updateNativeResolution = async (input) => {
  const { data, errorMessage } = await runGraphql(
    graphqlOperation(customMutations.updateNativeResolution, { input }),
    "updateNativeResolution"
  );

  return { data, errorMessage };
};

export const getRequiredResolution = async (id) => {
  const { data, errorMessage } = await runGraphql(
    graphqlOperation(customQueries.getRequiredResolution, { id }),
    "getRequiredResolution"
  );

  return { data, errorMessage };
};

export const listRequiredResolutions = async () => {
  let data = [];
  let nextToken;
  let errorMessage;

  do {
    const result = await runGraphql(
      graphqlOperation(queries.listRequiredResolutions, { nextToken }),
      "listRequiredResolutions",
      true
    );
    data = data.concat(result.data);
    nextToken = result.nextToken;
    if (result.errorMessage)
      errorMessage = errorMessage
        ? errorMessage + "\n\n" + result.errorMessage
        : result.errorMessage;
  } while (nextToken);

  return {
    data: data.filter((item) => !!item),
    errorMessage,
  };
};

export const createRequiredResolution = async (input) => {
  const { data, errorMessage } = await runGraphql(
    graphqlOperation(mutations.createRequiredResolution, { input }),
    "createRequiredResolution"
  );

  return { data, errorMessage };
};

// Check if required resolution with the given value and type exists
export const getRequiredResolutionByResAndType = async (input) => {
  const filter = {
    and: [{ value: { eq: input.value } }, { type: { eq: input.type } }],
  };

  const result = await runGraphql(
    graphqlOperation(queries.listRequiredResolutions, { filter }),
    "listRequiredResolutions.items"
  );

  return {
    data: result.data.filter((item) => !!item)[0],
    errorMessage: result.errorMessage,
  };
};

export const createCampaignRequiredResolutions = async (campaignID, requiredResolutions) => {
  for (const requiredResolutionID of requiredResolutions) {
    const input = { campaignID, requiredResolutionID };
    const { errorMessage } = await runGraphql(
      graphqlOperation(mutations.createCampaignRequiredResolution, { input }),
      "createCampaignRequiredResolution"
    );
    if (errorMessage) console.warn(errorMessage);
  }
};

export const createCampaignNativeResolutions = async (campaignID, nativeResolutions) => {
  for (const nativeResolutionID of nativeResolutions) {
    const input = { campaignID, nativeResolutionID };
    const { errorMessage } = await runGraphql(
      graphqlOperation(mutations.createCampaignNativeResolution, { input }),
      "createCampaignNativeResolution"
    );
    if (errorMessage) console.warn(errorMessage);
  }
};

export const deleteCampaignRequiredResolutions = async (ids) => {
  for (const id of ids) {
    const { errorMessage } = await runGraphql(
      graphqlOperation(mutations.deleteCampaignRequiredResolution, { input: { id } }),
      "deleteCampaignRequiredResolution"
    );
    if (errorMessage) console.warn(errorMessage);
  }
};

export const deleteCampaignNativeResolutions = async (ids) => {
  for (const id of ids) {
    const { errorMessage } = await runGraphql(
      graphqlOperation(mutations.deleteCampaignNativeResolution, { input: { id } }),
      "deleteCampaignNativeResolution"
    );
    if (errorMessage) console.warn(errorMessage);
  }
};

export const getAgency = async (id) => {
  const result = await runGraphql(graphqlOperation(queries.getAgency, { id }), "getAgency");

  return result;
};

export const listAgencys = async (agencyType) => {
  let data = [];
  let nextToken;
  let errorMessage;
  let filter;

  if (agencyType) {
    filter = { type: { eq: agencyType } };
  }

  do {
    const result = await runGraphql(
      graphqlOperation(queries.listAgencys, { filter, nextToken }),
      "listAgencys",
      true
    );
    data = data.concat(result.data);
    nextToken = result.nextToken;
    if (result.errorMessage)
      errorMessage = errorMessage
        ? errorMessage + "\n\n" + result.errorMessage
        : result.errorMessage;
  } while (nextToken);

  return {
    data: data.filter((item) => !!item),
    errorMessage: errorMessage,
  };
};

export const createAgency = async (input) => {
  const result = await runGraphql(
    graphqlOperation(mutations.createAgency, { input }),
    "createAgency"
  );

  return result;
};

export const updateAgency = async (input) => {
  const result = await runGraphql(
    graphqlOperation(mutations.updateAgency, { input }),
    "updateAgency"
  );

  return result;
};

export const sendAssetsToPrint = async (assets, printService, sender) => {
  try {
    const result = await API.post("CommonQueries", "/SendAssetsToPrint/", {
      body: {
        assets,
        printService,
        sender,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });

    return { data: result.message };
  } catch (err) {
    console.error("Error sending asset(s) to print: ", JSON.stringify(err, null, 2));
    return {
      errorMessage: t("send_assets_to_print_error", { ns: "common" }),
    };
  }
};

export const sendAssetsToStartprintAPI = async (
  campaignID,
  campaignName,
  orderlineID,
  sender,
  assets
) => {
  try {
    const result = await API.post("CommonQueries", "/sendToQueue/", {
      body: {
        event: "sendAssetsToStartprintAPI",
        campaign_id: campaignID,
        campaign_name: campaignName,
        orderline_id: orderlineID,
        sender,
        assets,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });

    return { data: result.message };
  } catch (err) {
    console.error("Error sending asset(s) to StartPrint API: ", JSON.stringify(err, null, 2));
    return {
      errorMessage: t("send_assets_to_startprint_error", { ns: "common" }),
    };
  }
};

export const sendInfoToCloudwatch = async (event, sender, assets) => {
  try {
    const result = await API.post("CommonQueries", "/eventLogger/", {
      body: {
        event,
        sender,
        assets,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });

    return { data: result.message };
  } catch (err) {
    console.error("Error sending asset(s) to eventLogger: ", JSON.stringify(err, null, 2));
    return {
      errorMessage: err,
    };
  }
};

export const sendAssetsToDigitalService = async (
  assets,
  digitalServices,
  sender,
  scheduling_notes
) => {
  try {
    const result = await API.post("CommonQueries", "/sendToExternalPartner/", {
      body: {
        assets,
        digitalServices,
        sender,
        scheduling_notes,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });

    return { data: result.message };
  } catch (err) {
    console.error("Error sending asset(s) to digital service(s): ", JSON.stringify(err, null, 2));
    return {
      errorMessage: t("send_assets_to_digital_service_error", { ns: "common" }),
    };
  }
};

export const listPrintSizes = async () => {
  let data = [];
  let nextToken;
  let errorMessage;

  do {
    const result = await runGraphql(
      graphqlOperation(customQueries.listPrintSizes, { nextToken }),
      "listPrintSizes",
      true
    );
    data = data.concat(result.data);
    nextToken = result.nextToken;
    if (result.errorMessage)
      errorMessage = errorMessage
        ? errorMessage + "\n\n" + result.errorMessage
        : result.errorMessage;
  } while (nextToken);

  return {
    data: data.filter((item) => !!item),
    errorMessage: errorMessage,
  };
};

export const createPrintSize = async (input) => {
  const result = await runGraphql(
    graphqlOperation(mutations.createPrintSize, { input }),
    "createPrintSize"
  );

  return result;
};

export const updatePrintSize = async (input) => {
  const result = await runGraphql(
    graphqlOperation(mutations.updatePrintSize, { input }),
    "updatePrintSize"
  );

  return result;
};

export const createCampaignPrintSizes = async (campaignID, printSizeIDs) => {
  for (const printSizeID of printSizeIDs) {
    const input = { campaignID, printSizeID };
    const { errorMessage } = await runGraphql(
      graphqlOperation(mutations.createCampaignPrintSize, { input }),
      "createCampaignPrintSize"
    );
    if (errorMessage) console.warn(errorMessage);
  }
};

export const deleteCampaignPrintSizes = async (ids) => {
  for (const id of ids) {
    const { errorMessage } = await runGraphql(
      graphqlOperation(mutations.deleteCampaignPrintSize, { input: { id } }),
      "deleteCampaignPrintSize"
    );
    if (errorMessage) console.warn(errorMessage);
  }
};

export const invokeQCForAsset = async (asset) => {
  try {
    await API.post("CommonQueries", "/invokeQC/", {
      body: {
        assetMedia: asset.media,
        assetID: asset.id,
        assetName: asset.name,
        campaignID: asset.campaignID,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });
    // Get updated asset
    const { data } = await getAsset(asset.id);
    return { data };
  } catch (err) {
    console.error("Error when invoking QC for asset: ", JSON.stringify(err, null, 2));
    return {
      errorMessage: "Invoking QC for asset failed.",
    };
  }
};

export const invokeCampaignStatusUpdate = async (campaignID) => {
  try {
    await API.post("CommonQueries", "/UpdateCampaignStatus/", {
      body: {
        campaignID,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });
    return {};
  } catch (err) {
    console.error("Error when invoking updating campaign status: ", JSON.stringify(err, null, 2));
    return {
      errorMessage: "Updating campaign status failed.",
    };
  }
};

export const updateCampaignMediaDeliveryPersonContacts = async (campaignID) => {
  try {
    const activeUser = await getActiveUser();

    await API.post(
      "CommonQueries",
      "/graphqlOperations/updateCampaignMediaDeliveryPersonContacts",
      {
        body: {
          campaignID,
          email: activeUser.data.email,
        },
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return {};
  } catch (err) {
    console.error(
      "Error updating campaign's media delivery person contacts: ",
      JSON.stringify(err, null, 2)
    );

    return {
      errorMessage: t("something_went_wrong", { ns: "common" }),
    };
  }
};

export const getCurrentAssetsStatusForView = async (campaignID) => {
  try {
    const result = await API.post(
      "CommonQueries",
      "/graphqlOperations/getCurrentAssetsStatusForContributorView",
      {
        body: {
          campaignID,
        },
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!Array.isArray(result.items) || !result.items) throw new Error("Invalid response body");
    return { response: result.items };
  } catch (err) {
    console.error("Error fetching campaign asset status: ", JSON.stringify(err, null, 2));

    return {
      errorMessage: t("something_went_wrong_required_asset_check", { ns: "common" }),
    };
  }
};

export const getZippedAssets = async (assetMediaArr, campaignID) => {
  const POLL_INTERVAL = 3000;
  const POLL_TIMEOUT = 300000;

  try {
    const startResult = await API.post("CommonQueries", "/ZipAssets/", {
      body: {
        assetMediaArr,
        campaignID,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });

    const jobId = startResult?.jobId;
    if (!jobId) {
      throw new Error("Missing ZIP job id in start response.");
    }

    const startTime = Date.now();

    while (Date.now() - startTime < POLL_TIMEOUT) {
      const statusResult = await API.post("CommonQueries", "/ZipAssets/", {
        body: {
          action: "status",
          jobId,
        },
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (statusResult?.status === "COMPLETED" && statusResult?.downloadUrl) {
        return statusResult.downloadUrl;
      }

      if (statusResult?.status === "FAILED") {
        throw new Error(statusResult?.errorMessage || "ZIP job failed.");
      }

      await sleep(POLL_INTERVAL);
    }

    throw new Error("Timed out while preparing ZIP assets.");
  } catch (err) {
    throw new Error(err?.message || JSON.stringify(err, null, 2));
  }
};

export const CSVExporter = async (data) => {
  try {
    const result = await API.post("CommonQueries", "/CSVExport/", {
      body: {
        data,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });
    return result;
  } catch (err) {
    throw new Error(JSON.stringify(err, null, 2));
  }
};

// Invoke processAssets state machine version.
export const invokeAssetSFNProcessing = async (data) => {
  try {
    const result = await API.post("CommonQueries", "/sfnProcessAssets/", {
      body: {
        event: "sfnProcessAssets",
        data,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });
    return result;
  } catch (err) {
    console.error("Error when processing assets: ", JSON.stringify(err, null, 2));
    return {
      errorMessage: "Processing assets failed.",
    };
  }
};

// Poll status of state machine execution so we are able to know when the process is complete.
export const pollProcessAssetsStatus = async (executionArn) => {
  try {
    const res = await API.post("CommonQueries", "/ProcessAssetsStatus", {
      body: { executionArn },
      headers: { "Content-Type": "application/json" },
    });
    return {
      status: res?.status,
      errorMessage: res?.errorMessage || null,
    };
  } catch (err) {
    console.error("Error polling ProcessAssets execution status: ", JSON.stringify(err, null, 2));
    return {
      status: null,
      errorMessage: "Polling ProcessAssets status failed.",
    };
  }
};

export const invokeCAPNotify = async (data) => {
  try {
    await API.post("CommonQueries", "/CAPNotification", {
      body: {
        data,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    console.error("Error while sending approverIDs to the server", JSON.stringify(error, null, 2));
    return {
      errorMessage: "Failed to notify approvers",
    };
  }
};

// Processed assets are being fetched between interval until they are all processed
export const waitForProcessedAssetStatus = async (campaign, timeout = 40000) => {
  const INTERVAL = 3000;
  const startTime = Date.now();
  let tryTime = 0,
    tries = 0;
  try {
    do {
      await sleep(INTERVAL);
      const { data, errorMessage } = await listAssets({
        filter: {
          campaignID: { eq: campaign.id },
          ...PROCESSED_ASSETS_FILTER,
        },
      });

      if (!data.filter((item) => item.status === "PROCESSING_PENDING").length > 0)
        return {
          data,
          errorMessage,
        };

      tryTime = Date.now() - startTime;
      // eslint-disable-next-line no-unused-vars
      tries++;
    } while (tryTime < timeout);
  } catch (err) {
    throw new Error(t("timeout_error", { ns: "common" }));
  }
};

/** APPROVER */
export const getApprover = async (id) => {
  let result, errorMessage;
  try {
    const response = await runGraphql(
      graphqlOperation(customQueries.getApprover, { id }),
      "getApprover"
    );
    result = response.data;
    errorMessage = response.errorMessage;
  } catch (error) {
    console.log("Error getting single approver", error);
  }

  return {
    data: result,
    errorMessage: errorMessage,
  };
};

export const listApprovers = async () => {
  let data = [];
  let nextToken;
  let errorMessage;

  do {
    const result = await runGraphql(
      graphqlOperation(customQueries.listApprovers, { nextToken }),
      "listApprovers",
      true
    );
    data = data.concat(result.data);
    nextToken = result.nextToken;
    if (result.errorMessage)
      errorMessage = errorMessage
        ? errorMessage + "\n\n" + result.errorMessage
        : result.errorMessage;
  } while (nextToken);

  return {
    data: data.filter((item) => !!item),
    errorMessage: errorMessage,
  };
};

export const createApprover = async (input) => {
  const result = await runGraphql(
    graphqlOperation(mutations.createApprover, { input }),
    "createApprover"
  );
  return result;
};

export const updateApprover = async (input) => {
  const result = await runGraphql(
    graphqlOperation(mutations.updateApprover, { input }),
    "updateApprover"
  );
  return result;
};
export const deleteApprover = async (id) => {
  const result = await runGraphql(
    graphqlOperation(mutations.deleteApprover, { input: { id: id } }),
    "deleteApprover"
  );
  return result;
};

export const getUserMatchingApprover = async (userID) => {
  let data = [];
  let nextToken;
  let errorMessage;

  do {
    const result = await runGraphql(
      graphqlOperation(customQueries.getApproverByUserId, { nextToken, userID: userID }),
      "listApprovers",
      true
    );
    data = data.concat(result.data);
    nextToken = result.nextToken;
    if (result.errorMessage)
      errorMessage = errorMessage
        ? errorMessage + "\n\n" + result.errorMessage
        : result.errorMessage;
  } while (nextToken);
  return {
    data: data.filter((item) => !!item),
    errorMessage: errorMessage,
  };
};

/** APPROVAL */
export const listApprovals = async () => {
  let data = [];
  let nextToken;
  let errorMessage;
  try {
    do {
      const result = await runGraphql(
        graphqlOperation(customQueries.listApprovals, { nextToken }),
        "listApprovals",
        true
      );
      data = [...data, ...result.data];
      nextToken = result.nextToken ? result.nextToken : null;
      if (result.errorMessage)
        errorMessage = errorMessage
          ? errorMessage + "\n\n" + result.errorMessage
          : result.errorMessage;
    } while (nextToken);
  } catch (error) {
    console.error("Error listing approvals:", error);
  }

  return {
    data: data.filter((item) => !!item),
    errorMessage: errorMessage,
  };
};

export const listApprovalsForContributor = async (approverID) => {
  let data = [];
  let nextToken;
  let errorMessage;

  try {
    do {
      const result = await runGraphql(
        graphqlOperation(customQueries.listApprovalsForContributor, {
          nextToken,
          approverID: approverID,
        }),
        "listApprovals",
        true
      );
      data = data.concat(result.data);
      nextToken = result.nextToken ? result.nextToken : null;
      if (result.errorMessage)
        errorMessage = errorMessage
          ? errorMessage + "\n\n" + result.errorMessage
          : result.errorMessage;
    } while (nextToken);
  } catch (err) {
    console.log("Could not get approvals for contributor:", err);
  }

  return {
    data: data.filter((item) => !!item),
    errorMessage: errorMessage,
  };
};

export const createApproval = async (input) => {
  let result;
  try {
    result = await runGraphql(
      graphqlOperation(mutations.createApproval, { input }),
      "createApproval"
    );
  } catch (error) {
    console.log("error creating approval:", error);
  }
  return result;
};

/**
 * Create approval items for campaign
 * Each approval item needs at least approverID, assetID, campaignID, and status fields
 * Approval status is either APPROVED | PENDING | DECLINED
 * @param {*} campaign campaign designated for content approval process
 * @param {*} approverAssetMap object with approver id as key and their info + selected assets as respective value
 * @param {*} deadline content approval process deadline
 * @param {*} campaignViewerIds string array of contributor user ids who need to see the campaign (FOR NOW)
 */
export const createApprovals = async (campaign, approverAssetMap, deadline, campaignViewerIds) => {
  // Check which assets of the campaign need viewers field updated form: { assetId: [userIds] }
  let UPDATE_VIEWERS_NEEDED = false;
  const ASSET_VIEWER_UPDATE_INFO = {};
  Object.keys(approverAssetMap).forEach((approverId) => {
    const approver = approverAssetMap[approverId].approver;
    const userId = approver.userID;
    approverAssetMap[approverId].selectedItems.forEach((asset) => {
      const cids = ASSET_VIEWER_UPDATE_INFO[asset.id] ?? [];
      cids.push(userId);
      ASSET_VIEWER_UPDATE_INFO[asset.id] = cids;
    });
  });

  // Helpers to check if approver is already selected for certain asset as approver
  const { data: campaignApprovals } = await getApprovalsOfCampaign(campaign.id);
  const isPairFound = (assetID, approverID) => {
    return campaignApprovals.some(
      (obj) => obj.assetID === assetID && obj.approverID === approverID
    );
  };

  // Gather data for approvals
  const year = deadline.getFullYear();
  const month = String(deadline.getMonth() + 1).padStart(2, "0");
  const day = String(deadline.getDate()).padStart(2, "0");
  const formattedDate = `${year}-${month}-${day}`;
  let result;
  const {
    id: campaignID,
    name: campaignName,
    start_time: campaignStartTime,
    end_time: campaignEndTime,
  } = campaign;
  const promises = [];

  for (const approverID in approverAssetMap) {
    const { selectedItems: assets } = approverAssetMap[approverID];
    for (const asset of assets) {
      // Only create if the approver is not already picked for the asset
      if (!isPairFound(asset.id, approverID)) {
        let input = {
          assetID: asset.id,
          campaignID: campaignID,
          campaignName: campaignName,
          startTime: asset.start_time !== null ? asset.start_time : campaignStartTime,
          endTime: asset.end_time !== null ? asset.end_time : campaignEndTime,
          approverID: approverID,
          deadline: formattedDate,
          status: "PENDING",
        };
        const approvalCreatePromise = createApproval(input);
        UPDATE_VIEWERS_NEEDED = true;
        promises.push(approvalCreatePromise);
      }
    }
  }

  // Give contributors read permission to asset so they can query their cap assets
  if (UPDATE_VIEWERS_NEEDED) {
    for (const assetId in ASSET_VIEWER_UPDATE_INFO) {
      const viewers = ASSET_VIEWER_UPDATE_INFO[assetId];
      const assetViewerUpdatePromise = updateAssetViewers(assetId, viewers);
      promises.push(assetViewerUpdatePromise);
    }
  }

  // Give contributors read permission to that campaign too
  const { data: campaignData } = await runGraphql(
    graphqlOperation(customQueries.getCampaignWithAssets, { id: campaign.id }),
    "getCampaign"
  );
  const campaignViewers = campaignData.viewers ?? [];
  const UPDATE_CAMPAIGN_VIEWERS_NEEDED = campaignViewerIds.some(
    (vId) => !campaignViewers.includes(vId)
  );
  if (UPDATE_CAMPAIGN_VIEWERS_NEEDED) {
    const campaignViewersUpdatePromise = updateCampaignViewers(campaign.id, [
      ...new Set(campaignViewers.concat(campaignViewerIds)),
    ]);
    promises.push(campaignViewersUpdatePromise);
  }

  // Await all async operations
  result = await Promise.all(promises);
  result = result.filter((item) => !!item);

  // Invoke notify function only if some new approvals were created and send only valid items
  if (result && result.length > 0) {
    const invokeCapParams = result
      .filter(
        (item) => "assetID" in item.data && "approverID" in item.data && "campaignID" in item.data
      )
      .map((item) => {
        return {
          assetID: item.data.assetID,
          approverID: item.data.approverID,
          campaignID: item.data.campaignID,
        };
      });
    if (invokeCapParams.length > 0) {
      invokeCAPNotify(invokeCapParams);
    }
  }
  return result;
};

export const updateApproval = async (input) => {
  let result;
  try {
    result = await runGraphql(
      graphqlOperation(mutations.updateApproval, { input }),
      "updateApproval"
    );
  } catch (error) {
    console.log("Error updating single approval:", error);
  }
  return result;
};

export const updateApprovals = async (approvalIds, status, reason) => {
  let result;
  try {
    const promises = [];

    for (const approvalId of approvalIds) {
      let input = {
        id: approvalId,
        status: status,
        reason: reason,
      };
      const updateApprovalPromise = await updateApproval(input);
      promises.push(updateApprovalPromise);
    }

    result = await Promise.all(promises);
  } catch (error) {
    console.log("Error approving multiple approvals:", error);
  }
  return result;
};

export const deleteApproval = async (id) => {
  const result = await runGraphql(
    graphqlOperation(mutations.deleteApproval, { input: { id: id } }),
    "deleteApproval"
  );
  return result;
};

export const approveWholeCampaign = async (campaignId) => {
  const { data: campaignApprovals } = await getApprovalsOfCampaign(campaignId);
  const campaignApprovalsIds = campaignApprovals.map((a) => a.id);
  let resultItems = [];
  let errorMessage;

  for (const approvalId of campaignApprovalsIds) {
    try {
      const updateResult = await runGraphql(
        graphqlOperation(mutations.updateApproval, {
          input: { id: approvalId, status: "APPROVED" },
        }),
        "updateApproval"
      );
      resultItems.push(updateResult.data);
      errorMessage = updateResult.errorMessage;
    } catch (error) {
      console.log("Error updating single approval in update whole campaign:", error);
    }
  }

  return {
    data: resultItems,
    errorMessage,
  };
};

export const rejectWholeCampaign = async (campaignId) => {
  const { data: campaignApprovals } = await getApprovalsOfCampaign(campaignId);
  const pendingCampaignApprovalsIds = campaignApprovals
    .filter((approval) => approval.status === "PENDING")
    .map((approval) => approval.id);
  let resultItems = [];
  let errorMessage;

  for (const approvalId of pendingCampaignApprovalsIds) {
    try {
      const updateResult = await runGraphql(
        graphqlOperation(mutations.updateApproval, {
          input: { id: approvalId, status: "REJECTED" },
        }),
        "updateApproval"
      );
      resultItems.push(updateResult.data);
      errorMessage = updateResult.errorMessage;
    } catch (error) {
      console.log("Error updating single approval in reject whole campaign:", error);
    }
  }

  return {
    data: resultItems,
    errorMessage,
  };
};
